/**
 * MUI v5 Typography Theme
 *
 * Variant naming: {family}-{weight}-{size}[-{modifier}]
 *   weight   = exact CSS numeric value (300 / 400 / 500 / 600 / 700 / 800 / 900)
 *   size     = px value from design JSON
 *   modifier = uppercase | strikethrough | underline (only when present in JSON)
 *
 * Examples:
 *   "roboto-600-18-uppercase"   — Roboto SemiBold 18px uppercase
 *   "gazpacho-900-50"           — Gazpacho Black 50px
 *   "montserrat-800-12-uppercase" — Montserrat ExtraBold 12px uppercase label
 *
 * Prop-based API (always wins over variant styles):
 *   <Typography family="roboto" size={18} weight={600} uppercase />
 *   <Typography family="gazpacho" size={50} color="#001884" />
 *   <Typography size={14} strike ellipsis color="#717171" />
 *
 * OPEN ITEMS:
 *   · Roboto 600 — not in standard Google Fonts; use @fontsource/roboto
 *   · Montserrat weight 653 — needs variable font; browser rounds to 700
 *   · Gazpacho-Regular and Gazpacho-Black may be distinct font files
 *   · Load all weights via @font-face for Agrandir, Road Rage, Gazpacho, Inter
 */

import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles';
import { merge } from 'lodash';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';
import { formHelperTextClasses } from '@mui/material/FormHelperText';
import { mapTenantThemeToOverrides } from '@config/theme/mapTenantTheme';
import type { CustomerThemeOverrides } from '@services/tenantConfig.types';
import type { FontFamily, FontWeight } from '@config/theme/theme.types';
import {
  MS,
  semanticTypographyVariants,
  typographyStyleOverrides,
  typographyVariants,
} from '@config/theme/typography.config';
import { novusTokens, buildNovusComponentOverrides, novusBreakpoints, pageFabClearance, mobileDown } from '@config/theme/novus.tokens';
import { theme as symphonaTheme } from '@symphona/ui-kit';
import gazpachoBlackUrl from '@assets/fonts/Gazpacho-Black.otf';
import agrandirNarrowMediumUrl from '@assets/fonts/Agrandir Narrow Medium.ttf';

// `mixins.page` is a plain object in the second createTheme arg, so `theme` is not
// yet in scope. The FAB clearance is the shared `pageFabClearance` const from
// novus.tokens, derived from the FAB dimensions (size + offset + breathing room),
// never a new hardcoded token (PD-2). The pageFullBleed Paper variant binds to the
// same const so the clearance can never drift between this mixin and the surface.

// breakpoints.values must live in the FIRST createTheme argument: MUI only runs
// arg-0's breakpoints through createBreakpoints (which regenerates up/down/between);
// breakpoints passed in a later argument would deep-merge the values but leave the
// query helpers closed over the defaults. See R6 / theme.breakpoints.test.ts.
// Arg-0 of createTheme (see the breakpoints note above — must stay in arg-0).
const baseThemeInput: ThemeOptions = {
  ...(symphonaTheme as ThemeOptions),
  breakpoints: { values: { ...novusBreakpoints } },
};

// Arg-1 of createTheme: the CPQ token namespaces, palette extensions, mixins,
// typography and component overrides. Extracted to a const so `createAppTheme`
// can deep-merge per-tenant overrides on top of it.
const baseThemeOverrides = {
    novus: novusTokens,

    palette: {
      text: {
        primary: symphonaTheme.colors.primary,
        muted: novusTokens.mutedText,
        onCartFooter: novusTokens.cartFooterColor,
        onAddressHeader: novusTokens.addressHeaderColor,
        discountMessage: novusTokens.discountMessageColor,
        quantityLabel: novusTokens.quantityLabelColor,
      },
      brand: {
        primary: novusTokens.primaryBrandBlue,
        heading: novusTokens.headingBlue,
        navy: novusTokens.navy,
      },
      accent: {
        cyan: novusTokens.accentCyan,
        cyanBright: novusTokens.accentCyanBright,
        cyanAccent: novusTokens.cyanAccent,
        cyanSelected: novusTokens.cyanSelectedBorder,
        highlightIcon: novusTokens.highlightIconColor,
        orderSummaryDivider: novusTokens.orderSummaryDivider,
        changeAddress: novusTokens.changeAddressBtnColor,
        changeAddressHover: novusTokens.changeAddressBtnHoverBg,
      },
      surface: {
        white: novusTokens.sectionBgWhite,
        sectionBlue: novusTokens.sectionBgBlue,
        cyanLight: novusTokens.bgCyanLight,
        cyanLighter: novusTokens.bgCyanLighter,
        cyanDark: novusTokens.bgCyanDark,
        dialog: novusTokens.dialogBg,
        placeholder: novusTokens.placeholderBg,
        cartFooter: novusTokens.cartFooterBg,
        addressHeader: novusTokens.addressHeaderBg,
        addressHeaderGradient: novusTokens.addressHeaderGradient,
        cardSelectedBadge: novusTokens.cardSelectedBadgeBg,
        channelTooltipShadow: novusTokens.channelTooltipShadow,
      },
      border: {
        black: novusTokens.borderBlack,
        disabled: novusTokens.borderDisabled,
        default: novusTokens.defaultBorder,
        defaultHover: novusTokens.defaultHoverBorder,
        header: novusTokens.headerBorder,
        channelLogo: novusTokens.channelLogoBorder,
        channelLogoHover: novusTokens.channelLogoBorderHover,
        cardDefault: novusTokens.cardBorderDefault,
        cardSelected: novusTokens.cardBorderSelected,
        dialogSection: novusTokens.dialogSectionBorder,
        quantityCounter: novusTokens.quantityCounterBorder,
        discountBadge: novusTokens.discountBadgeBorder,
        equipmentDefault: novusTokens.equipmentCardBorderDefault,
        equipmentSelected: novusTokens.equipmentCardBorderSelected,
        equipmentHover: novusTokens.equipmentCardBorderHover,
        addOnDefault: novusTokens.addOnCardBorderDefault,
        addOnSelected: novusTokens.addOnCardBorderSelected,
        addOnHover: novusTokens.addOnCardBorderHover,
        editPlan: novusTokens.editPlanBorder,
        purpleOutlined: novusTokens.novusPurpleOutlinedBorder,
      },
      feedback: {
        discountBg: novusTokens.discountBadgeBg,
        discountTag: novusTokens.discountTagColor,
        discountGreen: novusTokens.discountGreen,
        couponApplied: novusTokens.couponAppliedAccent,
        error: novusTokens.errorRed,
        errorBannerBg: novusTokens.errorBannerBg,
        errorIcon: novusTokens.errorIcon,
        warningBg: novusTokens.warningBg,
        warningText: novusTokens.warningText,
        warningIcon: novusTokens.warningIconColor,
        warningDialogIcon: novusTokens.warningDialogIconColor,
      },
      interactive: {
        link: novusTokens.linkColor,
        linkHover: novusTokens.linkHoverColor,
        navCartHover: novusTokens.navBarCartHoverBg,
        checkoutDisabled: novusTokens.checkoutDisabledBg,
        selectPlanBg: novusTokens.selectPlanBg,
        selectPlanHover: novusTokens.selectPlanHoverBg,
        selectPlanText: novusTokens.selectPlanColor,
        editPlanBg: novusTokens.editPlanBg,
        editPlanText: novusTokens.editPlanColor,
        checkoutBg: novusTokens.checkoutBg,
        checkoutHover: novusTokens.checkoutHoverBg,
      },
      purple: {
        primary: novusTokens.purplePrimary,
        check: novusTokens.purpleCheck,
        bg: novusTokens.novusPurpleBg,
        text: novusTokens.novusPurpleColor,
        hoverBg: novusTokens.novusPurpleHoverBg,
        containedHoverBg: novusTokens.novusPurpleContainedHoverBg,
        btnBg: novusTokens.btnPurpleBg,
        btnHoverBg: novusTokens.btnPurpleHoverBg,
      },
    },

    shape: {
      card: novusTokens.cardBorderRadius,
      dialog: novusTokens.dialogBorderRadius,
      button: novusTokens.buttonRadius,
      equipmentCard: novusTokens.equipmentCardRadius,
      addOnCard: novusTokens.addOnCardRadius,
      quantityCounter: novusTokens.quantityCounterRadius,
    },

    mixins: {
      layout: {
        cardPadding: novusTokens.cardPadding,
        cardShadow: novusTokens.cardBoxShadow,
        cardTransition: novusTokens.cardTransition,
        highlightIconSize: novusTokens.highlightIconSize,
        cartFooterHeight: novusTokens.cartFooterHeight,
        footerHeight: novusTokens.novusFooterHeight,
        fabSize: novusTokens.novusFabSize,
        fabOffset: novusTokens.novusFabOffset,
        fabBannerOffset: novusTokens.novusFabBannerOffset,
        dialogMaxWidth: novusTokens.dialogMaxWidth,
        dialogMaxHeight: novusTokens.dialogMaxHeight,
        dialogHeaderHeight: novusTokens.dialogHeaderHeight,
        dialogFooterHeight: novusTokens.dialogFooterHeight,
        dialogSectionPadding: novusTokens.dialogSectionPadding,
        dialogContentPadding: novusTokens.dialogContentPadding,
        dialogContentGap: novusTokens.dialogContentGap,
        closeButtonSize: novusTokens.closeButtonSize,
        closeButtonHoverOpacity: novusTokens.closeButtonHoverOpacity,
        errorBannerGap: novusTokens.errorBannerGap,
        equipmentImageSize: novusTokens.equipmentImageSize,
        equipmentCardPadding: novusTokens.equipmentCardPadding,
        equipmentCardGap: novusTokens.equipmentCardGap,
        equipmentCardDetailsGap: novusTokens.equipmentCardDetailsGap,
        addOnCardPadding: novusTokens.addOnCardPadding,
        addOnCardGap: novusTokens.addOnCardGap,
        addOnCardContentGap: novusTokens.addOnCardContentGap,
        addOnCheckboxSize: novusTokens.addOnCheckboxSize,
        addOnDiscountBadgePadding: novusTokens.addOnDiscountBadgePadding,
        addOnDiscountBadgeHeight: novusTokens.addOnDiscountBadgeHeight,
        buttonTransition: novusTokens.buttonTransition,
        buttonHoverOpacity: novusTokens.buttonHoverOpacity,
        modalBackdropBlur: novusTokens.modalBackdropBlur,
        actionButtonPadding: novusTokens.actionButtonPadding,
        skipButtonPadding: novusTokens.skipButtonPadding,
      },
      page: {
        maxWidth: '1440px',
        margin: '0 auto',
        sideMargin: '30px',
        padding: '30px',
        paddingMobile: '20px',
        // Single source of truth for every full-bleed band's horizontal padding.
        // All four page bands (nav, hero, plans, footer) consume this so their
        // centred Container content shares one left/right edge at every width.
        // Small below desktop so 2-up plan cards stay >=330px and never overflow.
        gutter: { xs: '20px', sm: '20px', md: '24px', lg: '40px', xl: '100px' },
        fabClearance: pageFabClearance,
      },
    },

    typography: {
      fontFamily: MS,
      htmlFontSize: 16,
      fontSize: 16,

      ...typographyVariants,
      ...semanticTypographyVariants,
    },
    components: {
      // Shared page content column (R7). Full-bleed page bands stay edge-to-edge;
      // their content sits in a Container that caps at the page max-width and centres,
      // so the band colour fills the sides above the cap while content stays centred.
      // maxWidth is disabled as a prop and gutters off so each band keeps its own
      // horizontal padding; the cap/centre come from mixins.page in one place.
      MuiContainer: {
        defaultProps: {
          maxWidth: false,
          disableGutters: true,
        },
        styleOverrides: {
          // Container already centres itself with margin-left/right: auto; only the
          // width cap needs to come from the shared page primitive.
          root: ({ theme }: { theme: Theme }) => ({
            width: '100%',
            maxWidth: theme.mixins.page.maxWidth,
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            fontFamily: 'Roboto',
            '& fieldset': {
              border: `1px solid ${symphonaTheme.colors.inputBorder}`,
              borderRadius: '10px',
            },
            '& .MuiFormHelperText-root': {
              fontFamily: 'Roboto',
              marginLeft: '0px',
              marginTop: '0px',
              opacity: 0,
            },
            '&:has(.Mui-error) .MuiFormHelperText-root': {
              opacity: 1,
            },
            '&:has(.Mui-error) fieldset': {
              border: `1px solid ${symphonaTheme.colors.validationError}`,
            },

            [`& .${formHelperTextClasses.root}`]: {
              fontFamily: 'Roboto',
              marginLeft: '0px',
              marginTop: '6px',
              fontSize: '16px',
            },

            // LeadCaptureForm text-input opt-in seam (slice-21d MC-10), byte-identical
            // home for the textInputSx object in LeadCaptureForm.styles.ts. The two
            // descendant selectors resolve from the same .MuiTextField-root the global
            // seam above keys from, so the selector context is preserved exactly. The
            // 6px radius nests under `& fieldset` (single-line inputs); the placeholder
            // typography matches the multiline twin below. Scoped to the className so it
            // stays off every other field until the components opt in. UNCONSUMED until a
            // later micro-cycle swaps the component over to className="lead-capture-text-input".
            '&.lead-capture-text-input': {
              '& .MuiOutlinedInput-input::placeholder': { fontSize: '16px', fontWeight: 400, lineHeight: '100%' },
              '& .MuiOutlinedInput-root': { '& fieldset': { borderRadius: '6px' } },
            },

            // LeadCaptureForm multiline-input opt-in seam (slice-21d MC-10), byte-identical
            // home for the multilineInputSx object in LeadCaptureForm.styles.ts. Same
            // placeholder typography as the text-input twin above, but the 6px radius sits
            // directly on `& .MuiOutlinedInput-root` (the multiline textarea renders its
            // radius at the root, not on a fieldset). Scoped to the className. UNCONSUMED
            // until a later micro-cycle swaps the component over to className="lead-capture-multiline-input".
            '&.lead-capture-multiline-input': {
              '& .MuiOutlinedInput-input::placeholder': { fontSize: '16px', fontWeight: 400, lineHeight: '100%' },
              '& .MuiOutlinedInput-root': { borderRadius: '6px' },
            },
          },
        },
      },
      MuiPopper: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            '&.MuiAutocomplete-popper': {
              '& .MuiPaper-root': {
                fontFamily: 'Roboto',
                marginTop: '4px',
                borderRadius: '6px',
                boxShadow: theme.novus.autocompleteShadow,

                '& .MuiAutocomplete-option.Mui-focused': {
                  backgroundColor: theme.novus.autocompleteOptionBg,
                },
              },
            },
          }),
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            '& .MuiOutlinedInput-root': {
              minHeight: '41px',
              height: 'auto',
              maxHeight: 'none',
              padding: '0 10px',
              borderRadius: '6px',
              backgroundColor: 'white',
              fontFamily: 'Roboto',
              fontSize: '16px',
              color: symphonaTheme.colors.primary,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.novus.inputBorderColor,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: symphonaTheme.colors.primary,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: symphonaTheme.colors.primary,
                borderWidth: '1px',
              },
            },
            '& .MuiAutocomplete-input': {
              padding: '0 !important',
              fontFamily: 'Roboto',
              fontSize: '16px',
              color: symphonaTheme.colors.primary,
            },
            '& .MuiAutocomplete-input::placeholder': {
              color: theme.novus.textSubtle,
              fontWeight: 400,
              opacity: 1,
            },
            '& .MuiAutocomplete-endAdornment': {
              right: '8px',
            },
            '& .MuiAutocomplete-popupIndicator': {
              color: symphonaTheme.colors.primary,
              transition: 'transform 0.2s ease',
            },
            '& .MuiAutocomplete-clearIndicator': {
              color: symphonaTheme.colors.primary,
            },
            '&:has(.Mui-error) .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
              borderColor: symphonaTheme.colors.validationError,
            },
            '&:has(.Mui-error) .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: symphonaTheme.colors.validationError,
            },
            '&:has(.Mui-error) .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: symphonaTheme.colors.validationError,
            },
            '&:has(.Mui-error) .MuiAutocomplete-popupIndicator': {
              color: symphonaTheme.colors.validationError,
            },
            '&.Mui-disabled .MuiOutlinedInput-root': {
              backgroundColor: symphonaTheme.colors.disabledBtnBg,
              cursor: 'not-allowed',
            },
          }),
          listbox: ({ theme }: { theme: Theme }) => ({
            '& .MuiAutocomplete-option': {
              fontFamily: 'Roboto',
              fontSize: '14px',
              fontWeight: 400,
              color: symphonaTheme.colors.primary,
              cursor: 'pointer',
              '&[aria-selected="true"]': {
                backgroundColor: theme.novus.autocompleteOptionBg,
              },
              '&.Mui-focused': {
                backgroundColor: theme.novus.autocompleteOptionBg,
              },
              '&:active': {
                backgroundColor: symphonaTheme.colors.primary,
                color: 'white',
              },
            },
            '& .MuiAutocomplete-option:has(.MuiCheckbox-root)[aria-selected="true"]': {
              backgroundColor: 'transparent',
            },
          }),
          tag: ({ theme }: { theme: Theme }) => ({
            fontFamily: 'Roboto',
            fontSize: '14px',
            backgroundColor: theme.novus.autocompleteOptionBg,
            color: symphonaTheme.colors.primary,
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ ownerState }: { ownerState: Record<string, unknown> }) => {
            const borderColor = (ownerState.borderColor as string | undefined) ?? symphonaTheme.colors.inputBorder;
            const activeBorderColor =
              (ownerState.activeBorderColor as string | undefined) ?? symphonaTheme.colors.black;
            const isMultiline = ownerState.multiline as boolean | undefined;
            return {
              borderRadius: '6px',
              maxHeight: isMultiline ? 'none' : 41,
              height: isMultiline ? 'auto' : 41,
              ...(isMultiline && { padding: '12px' }),
              [`&.${outlinedInputClasses.notchedOutline}`]: {
                borderColor,
                borderWidth: 1,
              },
              [`& .${outlinedInputClasses.input}::placeholder`]: {
                fontFamily: 'Roboto',
                lineHeight: '150%',
                color: symphonaTheme.colors.information,
                opacity: 1,
              },
              [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: activeBorderColor,
              },
              [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: activeBorderColor,
                borderWidth: 1,
              },
              [`&.Mui-error .${outlinedInputClasses.notchedOutline}`]: {
                border: `1px solid ${symphonaTheme.colors.validationError}`,
              },
            };
          },
        },
      },

      MuiCssBaseline: {
        styleOverrides: `
        @font-face {
          font-family: 'Gazpacho Black';
          src: url('${gazpachoBlackUrl}') format('opentype');
          font-style: normal;
          font-weight: 900;
          font-display: swap;
        }
        @font-face {
          font-family: 'Gazpacho';
          src: url('${gazpachoBlackUrl}') format('opentype');
          font-style: normal;
          font-weight: 400;
          font-display: swap;
        }
        @font-face {
          font-family: 'Agrandir';
          src: url('${agrandirNarrowMediumUrl}') format('truetype');
          font-style: normal;
          font-weight: 400;
          letter-spacing: 0;
          font-display: swap;
        }
      `,
      },
      MuiRadio: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            padding: 0,
            '&.Mui-checked': {
              color: theme.novus.checkboxColor,
            },
            '& .MuiSvgIcon-root': {
              fontSize: '18px',
              color: 'inherit',
            },
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }),
        },
      },
      ...buildNovusComponentOverrides(novusTokens),
      MuiTypography: {
        defaultProps: {
          component: 'span',
          color: symphonaTheme.colors.primary,
        },

        styleOverrides: {
          // `theme` here is always the fully-built Theme instance, never a partial one:
          // createAppTheme() (below) constructs `novus` and the rest of the theme in one
          // synchronous createTheme() call before this function ever runs — MUI only
          // invokes styleOverrides.root at render time, via ThemeProvider context, by
          // which point theme.novus has existed for the theme's entire lifetime. There is
          // no code path where a `theme.novus.*` read here can see `undefined`.
          //
          // Any className branch below that sets a colour under `&&` deliberately
          // out-specifies a plain instance `sx={{ color: ... }}` on that same element —
          // if a future caller adds one expecting it to win, it silently won't. Prefer
          // changing the token here (or adding a new className branch) over reaching for
          // instance-level sx on these classes.
          root: ({ ownerState, theme }: { ownerState: Record<string, unknown>; theme?: Theme }) => {
            // Tenant-merged tokens when MUI supplies the theme (always, at render time),
            // falling back to the static defaults otherwise. `theme` is optional because the
            // config-shape locks in theme.*.test.ts call this function as `root({ ownerState })`
            // with no theme -- a deliberate repo pattern. The fallback IS novusTokens, so the
            // resolved values are identical either way; only tenant overrides need `theme`.
            const t = theme?.novus ?? novusTokens;
            const {
              className,
              family,
              size,
              weight,
              lineHeight,
              color,
              italic,
              uppercase,
              capitalize,
              underline,
              strike,
              maxLines,
              ellipsis,
              variant,
            } = ownerState as typeof ownerState & {
              className?: string;
              family?: FontFamily;
              size?: number;
              weight?: FontWeight;
              lineHeight?: number;
              color?: string;
              italic?: boolean;
              uppercase?: boolean;
              capitalize?: boolean;
              underline?: boolean;
              strike?: boolean;
              maxLines?: number;
              ellipsis?: boolean;
              variant?: string;
            };

            const base = typographyStyleOverrides({
              family,
              size,
              weight,
              lineHeight,
              color,
              italic,
              uppercase,
              capitalize,
              underline,
              strike,
              maxLines,
              ellipsis,
            });

            if (variant === 'link.action') {
              return {
                ...base,
                fontWeight: 600,
                fontSize: '16px',
                fontFamily: 'Roboto',
                cursor: 'pointer',
                '&&': {
                  color: t.linkColor,
                },
                '&:hover': {
                  textDecoration: 'underline',
                },
              };
            }

            if (variant === 'inlineError') {
              return {
                ...base,
                cursor: 'pointer',
                fontSize: 'inherit',
                fontWeight: 600,
                lineHeight: '130%',
                verticalAlign: 'baseline',
                '&&': {
                  color: t.linkColor,
                  textDecoration: 'none',
                },
                '&&:hover': {
                  color: t.linkHoverColor,
                },
              };
            }

            if (variant === 'cartTotalLabel') {
              return {
                ...base,
                fontFamily: 'Roboto',
                fontWeight: 700,
                fontSize: '16px',
                // Cart Drawer -> "Total Label" drives this. It used to read
                // quantityLabelColor (a different field's token), so the Total Label
                // control did nothing. Falls back to quantityLabelColor, which is the
                // colour this variant has always rendered, so the default is unchanged.
                color: t.cartTotalLabelColor || t.quantityLabelColor,
              };
            }

            // Info-banner body text reads the brand navy. Applied on the instance
            // (out-specifying the default Typography colour with `&&`) rather than
            // as a Card descendant rule, because an ancestor→descendant colour rule
            // does not surface on the child's computed style. Scoped to the stable
            // InfoBanner-text class so the shared label.button / body.md roles are
            // untouched elsewhere.
            if (className?.includes('InfoBanner-text')) {
              return {
                ...base,
                '&&': {
                  color: t.novusPurpleColor,
                },
              };
            }

            // QuantityCounter value digit reads in borderBlack. Applied on the instance
            // (out-specifying the default Typography colour with `&&`) rather than as a
            // QuantityCounter Card descendant rule, because an ancestor→descendant colour
            // rule does not surface on the child's computed style. Scoped to the stable
            // QuantityCounter-valueText class so the shared body.md role is untouched
            // elsewhere. Mirrors the InfoBanner-text precedent above.
            if (className?.includes('QuantityCounter-valueText')) {
              return {
                ...base,
                '&&': {
                  color: t.borderBlack,
                },
              };
            }

            // DialogHeader title reads in borderBlack. Applied on the instance
            // (out-specifying the default Typography colour with `&&`) rather than as a
            // Paper descendant rule, because the default Typography colour prop wins over
            // an ancestor→descendant colour rule on the child's computed style. Scoped to
            // the stable DialogHeader-title class. Mirrors the InfoBanner-text /
            // QuantityCounter-valueText precedents above.
            if (className?.includes('DialogHeader-title')) {
              return {
                ...base,
                '&&': {
                  color: t.borderBlack,
                },
              };
            }

            // SideModalHeader title reads in borderBlack, replacing the old instance
            // `sx={{ color: common.black }}`. Applied on the instance (out-specifying
            // the default Typography colour with `&&`) rather than as a Paper descendant
            // rule, because the default Typography colour prop wins over an ancestor→
            // descendant colour rule on the child's computed style. Scoped to the stable
            // SideModalHeader-title class. Mirrors the DialogHeader-title precedent above.
            if (className?.includes('SideModalHeader-title')) {
              return {
                ...base,
                '&&': {
                  color: t.borderBlack,
                },
              };
            }

            // AddressHeader hero headings read the brand navy, replacing the old
            // instance `color={theme.novus.headingBlue}`. Applied on the instance
            // (out-specifying the default Typography colour with `&&`) because the
            // default Typography colour prop wins over an ancestor→descendant rule on
            // the child's computed style. Scoped to the stable AddressHeader-heading
            // class. Mirrors the DialogHeader-title / SideModalHeader-title precedents.
            if (className?.includes('AddressHeader-heading')) {
              return {
                ...base,
                // SC-12441: step the 50px hero headings down below the Tablet band so
                // the "Great News! / Novus Is Available…" hero isn't excessively tall on
                // mobile. font/layout at the branch root (only colour under &&), the same
                // pattern as the R14 review-order title below. Keyed to novusBreakpoints.md
                // so the desktop (≥769) 50px hero stays byte-identical. lineHeight is set
                // here because the instance lineHeight="105%" prop serialises to an invalid
                // "105%px" and is dropped, leaving the taller variant line-height.
                [`@media (max-width:${novusBreakpoints.md - 0.05}px)`]: {
                  fontSize: '32px',
                  lineHeight: '38px',
                },
                '&&': {
                  color: t.headingBlue,
                },
              };
            }

            // PlansSection section heading reads the brand navy, replacing the old
            // instance `color={theme.novus.headingBlue}`. Applied on the instance
            // (out-specifying the default Typography colour with `&&`) because the
            // default Typography colour prop wins over an ancestor→descendant rule on
            // the child's computed style. Scoped to the stable PlansSection-heading
            // class. Mirrors the AddressHeader-heading precedent above.
            if (className?.includes('PlansSection-heading')) {
              return {
                ...base,
                '&&': {
                  color: t.headingBlue,
                },
              };
            }

            // TopNavBar cart-count digit reads in cyanAccent, replacing the old instance
            // `color={theme.novus.cyanAccent}`. Applied on the instance (out-specifying
            // the default Typography colour with `&&`) because the default Typography
            // colour prop wins over an ancestor→descendant rule on the child's computed
            // style. Scoped to the stable NavTopBar-cartCount class. Mirrors the
            // AddressHeader-heading precedent above.
            if (className?.includes('NavTopBar-cartCount')) {
              return {
                ...base,
                '&&': {
                  color: t.cyanAccent,
                },
              };
            }

            // Configuration step-header titles read the brand navy, replacing the old
            // instance `color={theme.novus.navy}` repeated across the three step files.
            // Applied on the instance (out-specifying the default Typography colour with
            // `&&`) because the title.step variant's colour wins over an ancestor→
            // descendant rule on the child's computed style. Scoped to the stable
            // StepHeader-title class. Mirrors the NavTopBar-cartCount precedent above.
            if (className?.includes('StepHeader-title')) {
              return {
                ...base,
                '&&': {
                  color: t.navy,
                },
                // R16: on the Mobile band the add-mode step header swaps its full
                // copy for a compact one. Both strings render as sibling spans inside
                // the title; the active one is chosen purely by width here. The boundary
                // is the same novusBreakpoints-derived mobileDown key the dialog
                // full-page cutover uses (theme.breakpoints.down('sm'),
                // @media (max-width:480.95px)), so it moves with the scale.
                '& .StepHeader-title-full': {
                  [mobileDown]: { display: 'none' },
                },
                '& .StepHeader-title-compact': {
                  display: 'none',
                  [mobileDown]: { display: 'inline' },
                },
              };
            }

            // Add-ons step footer summary reads pure black, replacing the old
            // instance `color={theme.novus.borderBlack}`. Applied on the instance
            // (out-specifying the body.md variant colour with `&&`) because the
            // ambient DialogActions colour would otherwise win on the child's
            // computed style. Scoped to the stable AddOnsFooter-summary class.
            // Mirrors the StepHeader-title precedent above.
            if (className?.includes('AddOnsFooter-summary')) {
              return {
                ...base,
                '&&': {
                  color: t.borderBlack,
                },
              };
            }

            // ChargeSection total title reads the quantity-label slate, replacing the
            // old instance `color={theme.novus.quantityLabelColor}`. Applied on the
            // instance (out-specifying the price.total variant colour with `&&`)
            // because the variant root colour would otherwise win on the child's
            // computed style. Scoped to the stable ChargeSection-title class. Mirrors
            // the AddOnsFooter-summary precedent above.
            if (className?.includes('ChargeSection-title')) {
              return {
                ...base,
                '&&': {
                  color: t.quantityLabelColor,
                },
              };
            }

            // CheckoutErrorBanner title reads the error red, replacing the old
            // instance `sx={{ color: theme.novus.errorRed, lineHeight, mb }}`.
            // Applied on the instance (out-specifying the heading.subsection
            // variant with `&&`) because the inline sx previously won on
            // specificity; the doubled `&&` reproduces that so the migration is
            // byte-identical. Scoped to the stable CheckoutErrorBanner-title class.
            if (className?.includes('CheckoutErrorBanner-title')) {
              return {
                ...base,
                '&&': {
                  color: t.errorRed,
                  lineHeight: '130%',
                  marginBottom: '10px',
                },
              };
            }

            // CheckoutErrorBanner body reads pure black, replacing the old
            // instance `sx={{ color: theme.novus.borderBlack, lineHeight }}`.
            // Same `&&` reasoning as the title branch above. Scoped to the stable
            // CheckoutErrorBanner-body class.
            if (className?.includes('CheckoutErrorBanner-body')) {
              return {
                ...base,
                '&&': {
                  color: t.borderBlack,
                  lineHeight: '130%',
                },
              };
            }

            // LeadCaptureDialog submit-error headline reads the uppercase error red,
            // replacing the old instance `sx={{ fontWeight: '500', fontSize: '19px',
            // color: '#CF1010' }}`. The font props sit top-level; the colour is doubled
            // with `&&` because the ambient DialogActions / default Typography colour
            // would otherwise win on the child's computed style. Scoped to the stable
            // lead-capture-error-headline class. Mirrors the CheckoutErrorBanner-title
            // precedent above.
            if (className?.includes('lead-capture-error-headline')) {
              return {
                ...base,
                fontWeight: '500',
                fontSize: '19px',
                '&&': {
                  color: t.errorTextRed,
                },
              };
            }

            // LeadCaptureDialog submit-error body carries only the two font props,
            // replacing the old instance `sx={{ fontSize: '16px', fontWeight: '400' }}`.
            // No colour contest, so the props sit top-level with no `&&`. Scoped to the
            // stable lead-capture-error-body class.
            if (className?.includes('lead-capture-error-body')) {
              return {
                ...base,
                fontSize: '16px',
                fontWeight: '400',
              };
            }

            // LeadCaptureConfirmation "All Set!" title reads the brand navy and keeps its
            // 150% line-height, replacing the old instance `sx={{ lineHeight: '150%',
            // color: '#001884' }}`. The `&&` out-specifies the shared title.dialog variant's
            // own colour + line-height so they apply here without bleeding onto other
            // title.dialog consumers. Scoped to the stable lead-capture-confirmation-title
            // class. Mirrors the InfoDialog-title precedent above.
            if (className?.includes('lead-capture-confirmation-title')) {
              return { ...base, '&&': { color: t.primaryBrandBlue, lineHeight: '150%' } };
            }

            // LeadCaptureConfirmation body line keeps its 150% line-height and verbatim
            // `whiteSpace: 'wrap'`, replacing the old instance `sx={{ lineHeight: '150%',
            // whiteSpace: 'wrap' }}`. The `&&` reproduces the inline sx specificity over the
            // body.md variant's own line-height (24px) so the migration is byte-identical.
            // 'wrap' is a non-standard white-space value the browser ignores; transcribed
            // verbatim to preserve byte-identity — do not normalise it. Scoped to the stable
            // lead-capture-confirmation-body class. Mirrors the ServiceAddressDisplay-heading precedent.
            if (className?.includes('lead-capture-confirmation-body')) {
              return { ...base, '&&': { lineHeight: '150%', whiteSpace: 'wrap' } };
            }

            // LeadCaptureForm context message keeps its 130% line-height, replacing the old
            // instance `sx={{ lineHeight: '130%' }}`. The `&&` out-specifies the body.emphasis
            // variant's own line-height (24px) so the migration is byte-identical. Scoped to the
            // stable lead-capture-context-message class. Mirrors the lead-capture-confirmation-body
            // precedent above.
            if (className?.includes('lead-capture-context-message')) {
              return { ...base, '&&': { lineHeight: '130%' } };
            }

            // LeadCaptureForm "Customer Type" label keeps its 10px bottom gap and 135%
            // line-height, replacing the old instance `sx={{ mb: '10px', lineHeight: '135%' }}`.
            // The bottom gap is layout (sits top-level as marginBottom); the line-height goes
            // under `&&` to out-specify the label.fieldSmall variant's own line-height (21px) so
            // the migration is byte-identical. Scoped to the stable lead-capture-customer-type-label
            // class.
            if (className?.includes('lead-capture-customer-type-label')) {
              return { ...base, marginBottom: '10px', '&&': { lineHeight: '135%' } };
            }

            // LeadCaptureForm "Basic Service Request" label keeps its 150% line-height, replacing
            // the old instance `sx={{ lineHeight: '150%' }}`. The `&&` out-specifies the label.field
            // variant's own line-height (24px) so the migration is byte-identical. Scoped to the
            // stable lead-capture-basic-service-label class.
            if (className?.includes('lead-capture-basic-service-label')) {
              return { ...base, '&&': { lineHeight: '150%' } };
            }

            // LeadCaptureForm section headings keep their 150% line-height, replacing the old
            // instance `sx={{ lineHeight: '150%' }}`. The `&&` out-specifies the body.emphasis
            // variant's own line-height so the migration is byte-identical. A shared seam reused
            // by the ServiceAddress/Contact/Business section headings, scoped to the stable
            // lead-capture-section-heading class. Mirrors the lead-capture-basic-service-label
            // precedent above.
            if (className?.includes('lead-capture-section-heading')) {
              return { ...base, '&&': { lineHeight: '150%' } };
            }

            // PlanDetailsModal title reads the brand navy, replacing the old
            // instance `color={theme.novus.primaryBrandBlue}`. Applied on the
            // instance (out-specifying the title.plan variant colour with `&&`)
            // because the variant root colour would otherwise bleed onto PlanCard,
            // which shares title.plan. Scoped to the stable PlanDetailsModal-title
            // class. Mirrors the StepHeader-title precedent above.
            if (className?.includes('PlanDetailsModal-title')) {
              return {
                ...base,
                '&&': {
                  color: t.primaryBrandBlue,
                },
              };
            }

            // PlanDetailsModal price (hero + suffix) reads the brand navy,
            // replacing the old instance `color={theme.novus.primaryBrandBlue}`.
            // Applied on the instance (out-specifying the variant colour with `&&`)
            // because price.suffix is dual-role and also drives PriceSection's
            // PlanCard-price — colouring the variant root would double-paint it.
            // Scoped to the stable PlanDetailsModal-price class. Mirrors the
            // PlanDetailsModal-title precedent above.
            if (className?.includes('PlanDetailsModal-price')) {
              return {
                ...base,
                '&&': {
                  color: t.primaryBrandBlue,
                },
              };
            }

            // PhoneBillUpload helper description reads the quantity-label slate,
            // replacing the old instance `sx={{ lineHeight: '150%', color:
            // theme.novus.quantityLabelColor }}`. Applied on the instance
            // (out-specifying the body.sm variant with `&&`) because the inline sx
            // previously won on specificity; the doubled `&&` reproduces that so the
            // migration is byte-identical. Scoped to the stable PhoneBillUpload-description
            // class. Mirrors the CheckoutErrorBanner-body precedent above.
            if (className?.includes('PhoneBillUpload-description')) {
              return {
                ...base,
                '&&': {
                  color: t.quantityLabelColor,
                  lineHeight: '150%',
                },
              };
            }

            // InfoDialog heading reads the brand navy, replacing the old instance
            // `color={theme.novus.primaryBrandBlue}`. The `&&` out-specifies the
            // shared `title.dialog` variant colour so the navy applies here without
            // bleeding onto other dialogs that reuse that variant. Scoped to the
            // stable InfoDialog-title class. Mirrors the PlanDetailsModal-title precedent.
            if (className?.includes('InfoDialog-title')) {
              return {
                ...base,
                '&&': {
                  color: t.primaryBrandBlue,
                },
              };
            }

            // ServiceAddressDisplay heading carried only a `sx={{ lineHeight: '150%' }}`.
            // Re-homed onto the instance via `&&` (matching the inline sx specificity)
            // so the heading.subsection variant otherwise stays untouched. Scoped to the
            // stable ServiceAddressDisplay-heading class.
            if (className?.includes('ServiceAddressDisplay-heading')) {
              return { ...base, '&&': { lineHeight: '150%' } };
            }

            // ServiceAddressDisplay helper description reads the quantity-label slate,
            // replacing the old instance `sx={{ lineHeight: '150%', color: '#2D3A47' }}`.
            // The `&&` reproduces the inline sx specificity so the migration is
            // byte-identical. Scoped to the stable ServiceAddressDisplay-description class.
            if (className?.includes('ServiceAddressDisplay-description')) {
              return { ...base, '&&': { color: t.quantityLabelColor, lineHeight: '150%' } };
            }

            // ServiceAddressDisplay address line reads the same slate and keeps its
            // multi-line `pre-line` wrapping, replacing the old instance `sx={{ color:
            // '#2D3A47', whiteSpace: 'pre-line' }}`. Scoped to the stable
            // ServiceAddressDisplay-address class.
            if (className?.includes('ServiceAddressDisplay-address')) {
              return { ...base, '&&': { color: t.quantityLabelColor, whiteSpace: 'pre-line' } };
            }

            // FilePreviewCard file name reads the quantity-label slate, replacing the old
            // instance `sx={{ color: theme.novus.quantityLabelColor, lineHeight: '150%' }}`.
            // The `&&` reproduces the inline sx specificity over the label.fieldSmall variant
            // so the migration is byte-identical. Scoped to the stable FilePreviewCard-name
            // class. Mirrors the PhoneBillUpload-description precedent above.
            if (className?.includes('FilePreviewCard-name')) {
              return { ...base, '&&': { color: t.quantityLabelColor, lineHeight: '150%' } };
            }

            // FilePreviewCard date line reads the muted text colour, replacing the old
            // instance `sx={{ color: theme.novus.mutedText, lineHeight: '150%' }}`. The old
            // date label also carried an UNREGISTERED `variant="roboto-400-12"` that applied
            // no styles, so dropping it leaves the default body sizing untouched — the `&&`
            // colour + line-height are the only typography this branch contributes. Scoped to
            // the stable FilePreviewCard-date class.
            // Reads theme.novus.mutedText (tenant-merged), not the static novusTokens
            // import — mutedText is one of the GENERAL_TOKEN_MAP-driven tokens
            // (config field textMuted), so a tenant override must actually reach it here.
            if (className?.includes('FilePreviewCard-date')) {
              return { ...base, '&&': { color: t.mutedText, lineHeight: '150%' } };
            }

            // CouponSection heading reads the brand navy, replacing the old instance
            // `color={theme.novus.primaryBrandBlue}`. The `&&` out-specifies the shared
            // heading.detail variant colour so the navy applies here without bleeding onto
            // other headings that reuse that variant. Scoped to the stable CouponSection-title
            // class. Mirrors the InfoDialog-title precedent above.
            if (className?.includes('CouponSection-title')) {
              return { ...base, '&&': { color: t.primaryBrandBlue } };
            }

            // CouponSection applied-code label reads the coupon-applied green, replacing the
            // old instance `color={theme.novus.couponAppliedAccent}`. The `&&` out-specifies
            // the label.button variant colour so the green applies here without bleeding onto
            // other label.button consumers. Scoped to the stable CouponSection-appliedCode class.
            if (className?.includes('CouponSection-appliedCode')) {
              return { ...base, '&&': { color: t.couponAppliedAccent } };
            }

            // CouponSection Remove label reads the brand link colour, replacing the old
            // instance `color={theme.novus.linkColor}`. The `&&` out-specifies the label.field
            // variant colour so the purple applies here without bleeding onto other label.field
            // consumers. Scoped to the stable CouponSection-removeLabel class.
            if (className?.includes('CouponSection-removeLabel')) {
              return { ...base, '&&': { color: t.linkColor } };
            }

            // CouponSection "Click Here" span reads the brand link colour, replacing the old
            // instance `color={theme.novus.linkColor}`. The purple lives on the nested span
            // rather than the Link variant (which carries only the removed underline). The `&&`
            // out-specifies the label.fieldSmall variant colour. Scoped to the stable
            // CouponSection-learnMoreLink class. Checked before CouponSection-learnMore because
            // the latter is a substring of this class name.
            if (className?.includes('CouponSection-learnMoreLink')) {
              return { ...base, '&&': { color: t.linkColor } };
            }

            // CouponSection learn-more body line reads pure black, replacing the old instance
            // `color={theme.novus.borderBlack}`. The `&&` out-specifies the body.sm variant
            // colour so the black applies here without bleeding onto other body.sm consumers.
            // Scoped to the stable CouponSection-learnMore class.
            if (className?.includes('CouponSection-learnMore')) {
              return { ...base, '&&': { color: t.borderBlack } };
            }

            // Checkout OrderSummary section titles (the panel header, each ChargeSection
            // heading and the Total label) read the brand navy, replacing the old instance
            // `color={novusTokens.primaryBrandBlue}` sx on each. The `&&` out-specifies the
            // default Typography colour prop so the navy applies here without registering a
            // variant. Scoped to the stable OrderSummaryTitle class. Distinct string from the
            // cart-drawer ChargeSection-title branch above — no collision. Mirrors the
            // CouponSection-title precedent.
            if (className?.includes('OrderSummaryTitle')) {
              return { ...base, '&&': { color: t.primaryBrandBlue } };
            }

            // Checkout OrderSummary body lines (every charge/discount/tax/total amount row
            // and the CAD + prorated disclaimers) read pure black, replacing the old instance
            // `color: '#000000'` sx on each. The `&&` reproduces that specificity. Scoped to
            // the stable OrderSummaryText class. Mirrors the CouponSection-learnMore precedent.
            if (className?.includes('OrderSummaryText')) {
              return { ...base, '&&': { color: t.borderBlack } };
            }

            // CheckoutHeader review-order title reads white at a responsive size, replacing
            // the old instance font/weight/size + color:'#FFFFFF' sx. The `&&` out-specifies
            // the default Typography colour prop so the white applies without a variant.
            // The @media key is the same novusBreakpoints-derived sm-up boundary the band's
            // descendant rules use (481px), so the move is byte-identical. Scoped to the
            // stable CheckoutHeader-title class; no substring overlap with -cartLabel.
            if (className?.includes('CheckoutHeader-title')) {
              return {
                ...base,
                fontFamily: 'Roboto',
                fontWeight: 600,
                fontSize: '14px',
                // R14: the review-order title is dropped below the lg band (the single-
                // column checkout state hides it to reclaim header space) and returns at
                // lg-up where the two-column desktop layout has room. display is layout,
                // not colour, so it sits at the branch root (font/layout props top-level;
                // only colour under `&&`). The lg-up key is the same novusBreakpoints-
                // derived boundary (1025) the desktop layout keys off, so the ≥1025
                // appearance stays byte-identical.
                display: 'none',
                [`@media (min-width:${novusBreakpoints.lg}px)`]: { display: 'block' },
                [`@media (min-width:${novusBreakpoints.sm}px)`]: { fontSize: '18px' },
                // Reads theme.novus.addressHeaderColor (tenant-merged), not the static
                // novusTokens import — addressHeaderColor is GENERAL_TOKEN_MAP-driven
                // (config field addressHeaderColor), so a tenant override must actually
                // reach it here instead of silently freezing at the default.
                '&&': { color: t.addressHeaderColor },
              };
            }

            // CheckoutHeader "Go Back To Cart" label reads bright cyan and is hidden below
            // the sm breakpoint, replacing the old instance colour + display:{xs:'none',
            // sm:'block'} sx. The `&&` reproduces the colour-prop specificity; the @media key
            // is the same sm-up boundary, so the move is byte-identical. Scoped to the stable
            // CheckoutHeader-cartLabel class.
            // Reads theme.novus (tenant-merged at createAppTheme() call time), not the
            // static novusTokens import — this whole styleOverrides.root function is built
            // once as part of baseThemeOverrides, closing over novusTokens permanently, so
            // only the `theme` argument reflects per-tenant overrides. Every token read in
            // this function now goes through theme.novus for that reason; the ~118
            // palette-alias reads earlier in this file still use the static import, which
            // is correct by design (palette construction is meant to read the defaults,
            // and tenant overrides are merged in separately by mapTenantTheme.ts).
            if (className?.includes('CheckoutHeader-cartLabel')) {
              return {
                ...base,
                display: 'none',
                [`@media (min-width:${novusBreakpoints.sm}px)`]: { display: 'block' },
                '&&': { color: t.accentCyanBright },
              };
            }

            // PlanCard title, price (PriceSection), and discount tag ("LIMITED TIME OFFER"
            // badge label) all read via `&&` for the same reason: each was a plain
            // `& .PlanCard-*` descendant rule in novus.tokens.ts's planCard Paper variant.
            // In a real browser that descendant selector (2 classes, specificity 0,2,0)
            // actually BEATS MuiTypography's single-class default (0,1,0) regardless of
            // insertion order — so this was never a "dead selector" bug. The real bug is
            // that novus.tokens.ts's planCardBase closes over the static `novusTokens`
            // import (built once at module load), so a tenant override to primaryBrandBlue
            // never reached these three, even though the descendant rule itself rendered
            // fine. The `&&` re-home fixes THAT: it reads theme.novus (tenant-merged),
            // not the static import — see the CheckoutHeader-cartLabel comment above.
            // (An earlier version of this comment claimed the descendant rule was dead,
            // based on a vitest/jsdom getComputedStyle probe — jsdom's cascade
            // implementation doesn't model CSS specificity, only rule insertion order,
            // so that probe couldn't distinguish "dead selector" from "real browser
            // renders it correctly, jsdom doesn't." See the discountTagColor comment in
            // novus.tokens.ts for the corrected historical-value reasoning that follows.)
            if (className?.includes('PlanCard-title')) {
              return {
                ...base,
                '&&': { color: t.primaryBrandBlue },
              };
            }

            if (className?.includes('PlanCard-price')) {
              return {
                ...base,
                '&&': { color: t.primaryBrandBlue },
              };
            }

            // discountTagColor's default matches primaryBrandBlue's default (#001884) — the
            // tag shared PlanCard-price's descendant rule, and that rule genuinely applied
            // in a real browser (see the comment above); it never fell through to
            // MuiTypography's Typography default. Preserving that real historical value
            // here means a tenant with no themeConfig at all still renders the same colour
            // as before; only a tenant that explicitly sets discount.colorDiscountTag now
            // gets a different one.
            if (className?.includes('PlanCard-discountTag')) {
              return {
                ...base,
                '&&': { color: t.discountTagColor },
              };
            }

            // Order Confirmation heading reads the near-black heading colour at the bold
            //28px face, replacing the old instance font/weight/size + colour sx. The font
            // props sit at the branch root (out-specifying the default body1 variant, the
            // same shape as CheckoutHeader-title); the colour goes under `&&` to win over
            // the default Typography colour prop. Scoped to the stable
            // OrderConfirmationHeading class; no substring overlap with -Body / -Number.
            if (className?.includes('OrderConfirmationHeading')) {
              return {
                ...base,
                fontFamily: 'Roboto',
                fontWeight: 700,
                fontSize: '28px',
                '&&': { color: t.orderConfirmationHeading },
              };
            }

            // Order Confirmation body copy reads the muted body colour at the 16px regular
            // face with a 1.6 line-height and a 540px measure, replacing the old instance
            // font/weight/size/lineHeight/maxWidth + colour sx. Font props + lineHeight +
            // maxWidth sit at the branch root (out-specifying the default body1 variant);
            // the colour goes under `&&`. Scoped to the stable OrderConfirmationBody class.
            if (className?.includes('OrderConfirmationBody')) {
              return {
                ...base,
                fontFamily: 'Roboto',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: 1.6,
                maxWidth: '540px',
                '&&': { color: t.orderConfirmationBody },
              };
            }

            // Order Confirmation number line reads the heading colour at the 16px Roboto
            // face, replacing the old instance font/size + colour sx (the bold "Confirmation
            // Number" comes from the native <strong>, not this branch). Font props sit at
            // the branch root; the colour goes under `&&`. Scoped to the stable
            // OrderConfirmationNumber class.
            if (className?.includes('OrderConfirmationNumber')) {
              return {
                ...base,
                fontFamily: 'Roboto',
                fontSize: '16px',
                '&&': { color: t.orderConfirmationHeading },
              };
            }

            // Checkout step "Step N of M" label reads black at the Roboto 13px
            // face, replacing the old StepLabelText styled(Typography). The old styled
            // component carried no variant (so it sat on the default body1); the font props
            // here sit at the branch root and out-specify body1, the colour goes under `&&`
            // to win over the default Typography colour prop. Scoped to the stable
            // CheckoutStepLabel class; no substring overlap with the other Checkout seams.
            if (className?.includes('CheckoutStepLabel')) {
              return {
                ...base,
                fontFamily: 'Roboto',
                fontWeight: 400,
                fontSize: '13px',
                lineHeight: '150%',
                '&&': { color: t.borderBlack },
              };
            }

            // Checkout step title reads the brand navy at the bare gazpacho-regular 19px
            // face, replacing the old StepTitleText styled(Typography). The bare
            // 'gazpacho-regular' family string is intentional (there is no @font-face for it);
            // font props sit at the branch root, colour under `&&`. Scoped to the stable
            // CheckoutStepTitle class.
            if (className?.includes('CheckoutStepTitle')) {
              return {
                ...base,
                fontFamily: 'gazpacho-regular',
                fontWeight: 700,
                fontSize: '19px',
                lineHeight: '130%',
                '&&': { color: t.novusPurpleColor },
              };
            }

            // Checkout completed-step read-only field label reads the charcoal slate at the
            // Roboto 16px semibold face, replacing the old ReadOnlyLabel styled(Typography).
            // Font props sit at the branch root, colour under `&&`. Per-instance 700/14
            // overrides (InstallationStep ViewOnly) stay as residual sx on the consumer and
            // still win over these top-level props. Scoped to the stable CheckoutReadOnlyLabel
            // class.
            if (className?.includes('CheckoutReadOnlyLabel')) {
              return {
                ...base,
                fontFamily: 'Roboto',
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: '150%',
                '&&': { color: symphonaTheme.colors.charcoal },
              };
            }

            // Checkout completed-step read-only field value reads the charcoal slate at the
            // Roboto 16px regular face, replacing the old ReadOnlyValue styled(Typography).
            // Font props sit at the branch root, colour under `&&`. Scoped to the stable
            // CheckoutReadOnlyValue class.
            if (className?.includes('CheckoutReadOnlyValue')) {
              return {
                ...base,
                fontFamily: 'Roboto',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '150%',
                '&&': { color: symphonaTheme.colors.charcoal },
              };
            }

            // CheckAvailabilityDialog change_address_warning callout's inner "Warning:" label
            // reads the warningText amber, replacing the old instance
            // `color={theme.novus.warningText}`. The `&&` out-specifies MUI's default
            // Typography colour prop so the amber surfaces on the child's computed style.
            // Scoped to the globally-unique change-address-warning-text class; no substring
            // overlap with any other branch. Mirrors the InfoBanner-text precedent.
            if (className?.includes('change-address-warning-text')) {
              return { ...base, '&&': { color: t.warningText } };
            }

            return base;
          },
        },
      },
    },
};

/**
 * Builds the CPQ MUI theme, optionally re-themed by a tenant's DB-driven
 * `CustomerThemeOverrides`. The overrides are translated onto the current theme's
 * namespaces by {@link mapTenantThemeToOverrides} and deep-merged over the base.
 *
 * Called with no argument for the static default theme (tests, Storybook,
 * non-embedded default). The embedded app passes the resolved tenant overrides.
 */
/**
 * Patches every typography variant's fontFamily with the tenant's configured
 * font stacks. Required because each variant has a hardcoded fontFamily (e.g.
 * '"Agrandir", sans-serif') that overrides the root typography.fontFamily —
 * setting only the root has no visible effect on rendered variants.
 *
 * Agrandir (display heading) variants map to fontStacks.displayFont;
 * everything else maps to fontStacks.muiFontFamily ?? fontStacks.bodyFont.
 */
const buildFontTypographyPatch = (
  fontStacks: Partial<Record<string, string>>,
): Record<string, { fontFamily: string }> => {
  const bodyStack = fontStacks.muiFontFamily ?? fontStacks.bodyFont;
  const displayStack = fontStacks.displayFont ?? bodyStack;
  const headerStack = fontStacks.headerFont ?? bodyStack;
  const monoStack = fontStacks.monoFont ?? bodyStack;
  if (!bodyStack) return {};

  const patch: Record<string, { fontFamily: string }> = {};
  for (const [key, variant] of Object.entries({ ...typographyVariants, ...semanticTypographyVariants })) {
    const ff = (variant as { fontFamily?: string }).fontFamily;
    if (!ff) continue;
    const mapped = ff.includes('Gazpacho')
      ? (displayStack ?? bodyStack)
      : ff.includes('Agrandir')
        ? (headerStack ?? bodyStack)
        : ff.startsWith('"Roboto')
          ? (monoStack ?? bodyStack)
          : bodyStack;
    patch[key] = { fontFamily: mapped };
  }
  return patch;
};

export const createAppTheme = (overrides?: CustomerThemeOverrides): Theme => {
  if (!overrides) return createTheme(baseThemeInput, baseThemeOverrides);

  const mapperOutput = mapTenantThemeToOverrides(overrides);
  const effectiveNovusTokens = merge({}, novusTokens, mapperOutput.novus ?? {}) as typeof novusTokens;
  const fontPatch = overrides.fontStacks ? buildFontTypographyPatch(overrides.fontStacks) : {};
  const mergedOverrides = merge(
    {},
    baseThemeOverrides,
    { components: buildNovusComponentOverrides(effectiveNovusTokens) },
    mapperOutput,
    Object.keys(fontPatch).length ? { typography: fontPatch } : {},
  );
  return createTheme(baseThemeInput, mergedOverrides);
};

const theme = createAppTheme();

export default theme;
